/**
 * Contadores de contenido de lote (ListingCore entity_type=lot).
 * Agregados del backend: lot_content_count / total_lot_content / has_lot_content.
 */

import { getDisplayUnits } from './userLotUnits';

export const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Cards en getmylots con has_lot_content: true. */
export const countLotsWithContent = (lots) => (
  (Array.isArray(lots) ? lots : []).filter((lot) => lot?.has_lot_content === true).length
);

const formatLotContentSuffix = (lotContentCount) => {
  const count = toCount(lotContentCount);
  if (count <= 0) return '';
  return ` · ${count} con contenido de lote`;
};

/** Etiqueta en panel de listas: "N Lotes · N Unidades · N con contenido de lote". */
export const formatListMembershipCountsLabel = ({
  lotCount = 0,
  unitCount = 0,
  lotContentCount = 0,
} = {}) => {
  if (lotCount === 0 && unitCount === 0 && lotContentCount <= 0) {
    return 'Sin lotes · Sin unidades';
  }
  const lotsPart = lotCount === 0
    ? 'Sin lotes'
    : `${lotCount} Lote${lotCount !== 1 ? 's' : ''}`;
  const unitsPart = unitCount === 0
    ? 'Sin unidades'
    : `${unitCount} Unidad${unitCount !== 1 ? 'es' : ''}`;
  return `${lotsPart} · ${unitsPart}${formatLotContentSuffix(lotContentCount)}`;
};

/** Header REGISTROS: "N lotes · N unidades · N con contenido de lote". */
export const formatRegistrosCountsLabel = (
  lotCount,
  unitCount,
  lotContentCount = 0,
) => {
  const lotsPart = `${toCount(lotCount)} lote${lotCount !== 1 ? 's' : ''}`;
  const unitsPart = `${toCount(unitCount)} unidad${unitCount !== 1 ? 'es' : ''}`;
  return `${lotsPart} · ${unitsPart}${formatLotContentSuffix(lotContentCount)}`;
};

const pluralize = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Resumen del toolbar (Registros y detalle de lista):
 * - `label`: «N lotes · M registros», con M = unidades + contenido de lote
 *   («2128 lotes · 941 registros»). Si M es 0, solo «N lotes». Los lotes CAD se
 *   suman a los lotes antes de llamar (no son categoría aparte).
 * - `detail`: desglose de los registros («929 unidades · 12 con contenido de lote»);
 *   null si no hay registros.
 * - `tooltip`: resumen completo («2128 lotes · 929 unidades · 12 con contenido de
 *   lote»), así el tooltip también sirve si el label se corta con «…».
 * @returns {{ label: string, detail: string|null, tooltip: string }}
 */
export const formatRecordsSummary = (lotCount, unitCount = 0, lotContentCount = 0) => {
  const lots = Math.max(0, toCount(lotCount));
  const units = Math.max(0, toCount(unitCount));
  const content = Math.max(0, toCount(lotContentCount));
  const lotsPart = pluralize(lots, 'lote', 'lotes');
  const records = units + content;
  if (records === 0) return { label: lotsPart, detail: null, tooltip: lotsPart };
  const detail = [
    units > 0 || content === 0 ? pluralize(units, 'unidad', 'unidades') : null,
    content > 0 ? `${content} con contenido de lote` : null,
  ].filter(Boolean).join(' · ');
  return {
    label: `${lotsPart} · ${pluralize(records, 'registro', 'registros')}`,
    detail,
    tooltip: `${lotsPart} · ${detail}`,
  };
};

/** Conteos vivos desde getmylots?list_id= (detalle de lista). */
export const getListCountsFromGroupedLots = (detailLots) => {
  const lotIds = new Set();
  let unitCount = 0;
  let lotContentCount = 0;

  (Array.isArray(detailLots) ? detailLots : []).forEach((lot) => {
    if (lot?.lot_id) {
      lotIds.add(lot.lot_id);
      if (lot.has_lot_content === true) lotContentCount += 1;
    }
    unitCount += getDisplayUnits(lot).length;
  });

  return {
    lot_count: lotIds.size,
    item_count: unitCount,
    lot_content_count: lotContentCount,
  };
};

/** Normaliza conteos de GET /getuserlotlists. */
export const withListCountsFromApi = (lists) => (
  (lists || []).map((list) => ({
    ...list,
    lot_count: toCount(list.lot_count ?? list.lotCount),
    item_count: toCount(list.item_count ?? list.unit_count ?? list.unitCount),
    lot_content_count: toCount(list.lot_content_count ?? list.lotContentCount),
  }))
);
